<template>
  <span :title="tooltip">{{ name }}</span>
</template>

<script setup lang="ts">
import { computed, watch, watchEffect } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useMembersStore } from '@/stores/members'
import { useTenantsStore } from '@/stores/tenants'
import { memberName, resolveUserTooltip } from '@/utils/userDisplay'
import { logger } from '@/utils/logger'

/**
 * Presentational user name. Resolves a `uid` to a display name via the
 * project members, falling back to the **tenant** members. When a member doc has a
 * `display_name` it's used; otherwise the email is humanized (see userDisplay.ts).
 *
 * A raw uid is **never** rendered to the user: if there's no member doc with a
 * name/email to derive from, we show a neutral "Unknown user" placeholder and log
 * a warning so the data gap (an incomplete member doc) is surfaced to developers —
 * throwing would crash the whole view for what is only a data condition.
 *
 * Why not the `users` collection: `/users/{uid}` is owner-only readable in
 * Firestore, so a client can't read another user's name that way — hence the
 * member subcollections (project, then tenant) are the resolvable sources.
 */
const props = defineProps<{ uid: string }>()

const { t } = useI18n()
const membersStore = useMembersStore()
const tenantsStore = useTenantsStore()
const { projectMembers, tenantMembers } = storeToRefs(membersStore)

// Project members first so they take precedence when a uid is in both lists.
const known = computed(() => [...projectMembers.value, ...tenantMembers.value])
// Resolved name, or '' when no member doc has a name/email to derive one from.
const resolved = computed(() => memberName(known.value.find((m) => m.uid === props.uid)))

const name = computed(() => {
  if (resolved.value) return resolved.value
  // Members not loaded yet → render nothing rather than flashing the placeholder.
  if (!known.value.length) return ''
  return t('shared.unknown_user')
})
// The member's email (never the uid) is shown in the hover tooltip.
const tooltip = computed(() => resolveUserTooltip(props.uid, known.value))

// Best-effort: when the uid isn't a project member, make sure the tenant members
// (the fallback source) are loaded — some surfaces (e.g. directives) only load
// project members. `currentTenantId` MUST be a watched source: it can still be
// empty at mount, and without it the fallback would never fire once it resolves.
watch(
  [projectMembers, tenantMembers, () => props.uid, () => tenantsStore.currentTenantId],
  () => {
    if (projectMembers.value.some((m) => m.uid === props.uid)) return
    if (tenantMembers.value.length) return
    const tid = tenantsStore.currentTenantId
    if (tid) void membersStore.listTenantMembers(tid)
  },
  { immediate: true },
)

// Surface an unresolvable uid once (members are loaded but none carry a name/email).
let warned = false
watchEffect(() => {
  if (!warned && !resolved.value && known.value.length) {
    warned = true
    logger.warn(`UserName: no name/email for uid "${props.uid}" - member doc missing or incomplete`)
  }
})
</script>
